import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { List, Map } from 'immutable';
import isEqual from 'lodash/isEqual';
import { createSelector } from 'reselect';
import { trimTextToEmpty } from 'flow/utils/message-utils';
import { createFuseInstance, searchFuseInstance } from 'flow/utils/search-utils';
import { extractName } from 'flow/utils';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { createImmutableSelector } from 'flow/selectors/selector-utils';
var getFilterText = function getFilterText(_state, extensionFilter) {
  return trimTextToEmpty(extensionFilter);
};
var getExtensions = function getExtensions(state) {
  return state.extensions.get('extensions', Map());
};
var getExternalContacts = function getExternalContacts(state) {
  return state.contacts.get('externalContacts', Map());
};
var getThirdPartyContacts = function getThirdPartyContacts(state) {
  return state.contacts.get('socialIntegrationContacts', Map());
};
var FIELD_MAPPING = {
  firstname: 'name',
  lastname: 'name',
  anumber: 'fixedNumber',
  mobile: 'mobileNumber',
  alttelephone1: 'altTelephone1',
  alttelephone2: 'altTelephone2',
  address: 'address',
  postalcode: 'postalcode',
  country: 'country',
  city: 'city',
  email: 'email',
  description: 'description',
  department: 'department',
  occupation: 'occupation',
  keywords: 'keywords',
  companyname: 'companyName'
};
var FIELD_WEIGHTS = {
  name: 1.2,
  fixedNumber: 1.1,
  mobileNumber: 1.1,
  altTelephone1: 1.1,
  altTelephone2: 1.1,
  keywords: 1.0,
  companyName: 0.9,
  email: 0.8,
  occupation: 0.7,
  address: 0.6,
  country: 0.5,
  postalcode: 0.4,
  city: 0.3,
  description: 0.2,
  department: 0.1
};
var DEFAULT_WEIGHT = 1.0;
var getKeys = createSelector(function (state) {
  return state.customer.get('contactFields', List());
}, function (contactFields) {
  return contactFields.map(function (field) {
    return {
      name: FIELD_MAPPING[field.get('name')] || field.get('name'),
      weight: FIELD_WEIGHTS[FIELD_MAPPING[field.get('name')] || field.get('name')] || DEFAULT_WEIGHT
    };
  }).toArray();
});
var getMergedContactsList = createSelector([getExtensions, getExternalContacts, getThirdPartyContacts], function (extensions, externalContacts, thirdPartyContacts) {
  return extensions.merge(externalContacts).merge(thirdPartyContacts);
});

/* Opening a person's page writes that person again, often with equal data.
 * An entity that is the same, or equal, keeps its searchable object, so the
 * search does not build thousands of them again or run again. Each pass keeps
 * only the contacts it saw, so removed contacts, and a previous user's, go at
 * the next search. Until then this cache holds them. */
var searchableByKey = {};
var searchableFor = function searchableFor(entity, key, countryCodeForCustomer, previousByKey, nextByKey) {
  var cached = previousByKey[key];
  var searchable;
  if ((cached === null || cached === void 0 ? void 0 : cached.entity) === entity && cached.countryCodeForCustomer === countryCodeForCustomer) {
    searchable = cached.searchable;
  } else {
    var built = buildSearchable(entity, key, countryCodeForCustomer);
    searchable = cached && isEqual(cached.searchable, built) ? cached.searchable : built;
  }
  nextByKey[key] = {
    entity: entity,
    countryCodeForCustomer: countryCodeForCustomer,
    searchable: searchable
  };
  return searchable;
};
var buildSearchable = function buildSearchable(entity, key, countryCodeForCustomer) {
  if (!(entity !== null && entity !== void 0 && entity.get('key'))) {
    return null;
  }
  if (entity !== null && entity !== void 0 && entity.get('key').startsWith('contact') || entity !== null && entity !== void 0 && entity.get('key').startsWith('thirdpartycontact')) {
    return contactToSearchable(entity, key, countryCodeForCustomer);
  }
  if (entity !== null && entity !== void 0 && entity.get('key').startsWith('extension') && entity !== null && entity !== void 0 && entity.has('contact')) {
    return extensionToSearchable(entity, countryCodeForCustomer);
  }
  return null;
};
var getNumberFormatByCountryCode = function getNumberFormatByCountryCode(customerCountryCode, entity, type) {
  return customerCountryCode === entity.getIn([type, 'countryCode']) ? entity.getIn([type, 'nationalFormat']) : entity.getIn([type, 'number']);
};
var contactToSearchable = function contactToSearchable(contact, key, customerCountryCode) {
  var additionalFields = contact.get('additionalFields', Map()).toJS();
  return _objectSpread({
    key: key || contact.get('key'),
    name: extractName(contact),
    fixedNumber: getNumberFormatByCountryCode(customerCountryCode, contact, 'fixed'),
    mobileNumber: getNumberFormatByCountryCode(customerCountryCode, contact, 'mobile'),
    altTelephone1: contact.getIn(['altTelephone1', 'number']),
    altTelephone2: contact.getIn(['altTelephone2', 'number']),
    keywords: contact.get('keywords'),
    address: contact.get('address'),
    postalcode: contact.get('postalcode'),
    country: contact.get('country'),
    city: contact.get('city'),
    email: contact.get('email'),
    description: contact.get('description'),
    department: contact.get('department'),
    occupation: contact.get('occupation'),
    companyName: contact.get('companyName')
  }, additionalFields);
};
var extensionToSearchable = function extensionToSearchable(extension, customerCountryCode) {
  var additionalFields = extension.getIn(['contact', 'additionalFields'], Map()).toJS();
  return _objectSpread({
    key: extension.get('key'),
    name: extractName(extension.get('contact')),
    fixedNumber: getNumberFormatByCountryCode(customerCountryCode, extension.get('contact'), 'fixed'),
    mobileNumber: getNumberFormatByCountryCode(customerCountryCode, extension.get('contact'), 'mobile'),
    altTelephone1: extension.getIn(['contact', 'altTelephone1', 'number']),
    altTelephone2: extension.getIn(['contact', 'altTelephone2', 'number']),
    keywords: extension.getIn(['contact', 'keywords']),
    address: extension.getIn(['contact', 'address']),
    postalcode: extension.getIn(['contact', 'postalcode']),
    country: extension.getIn(['contact', 'country']),
    city: extension.getIn(['contact', 'city']),
    email: extension.getIn(['contact', 'email']),
    description: extension.getIn(['contact', 'description']),
    department: extension.getIn(['contact', 'department']),
    occupation: extension.getIn(['contact', 'occupation']),
    companyName: extension.getIn(['contact', 'companyName'])
  }, additionalFields);
};
var selectSearchables = createSelector([getMergedContactsList, selectUserCountryCode], function (contacts, countryCode) {
  var nextByKey = {};
  var searchables = contacts.map(function (entity, key) {
    return searchableFor(entity, key, countryCode, searchableByKey, nextByKey);
  }).valueSeq().filter(Boolean).toList();
  searchableByKey = nextByKey;
  return searchables;
});
var FUZZY_OPTIONS = {
  includeScore: true,
  includeMatches: true,
  shouldSort: true,
  useExtendedSearch: true
};

// While every searchable is the same object, the index and results stay. A
// searchable is a plain object, so Immutable's is compares it by reference.
var selectFuseInstance = createImmutableSelector([selectSearchables, getKeys], function (searchables, keys) {
  return createFuseInstance(searchables, function (searchable) {
    return searchable;
  }, FUZZY_OPTIONS, keys);
});
var selectFuzzySearchResults = createSelector([selectFuseInstance, getFilterText], function (fuseInstance, filterText) {
  return List(searchFuseInstance(fuseInstance, filterText));
});

// Without search text, nothing builds the index.
export var fuzzySearchColleaguesList = function fuzzySearchColleaguesList(state, extensionFilter) {
  return getFilterText(state, extensionFilter) ? selectFuzzySearchResults(state, extensionFilter) : List();
};